import { useState, useEffect } from 'react';
import { Bitcoin, Zap, TrendingUp, TrendingDown, Check, X, Layers, Activity } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import ConvictionSparkline from '../components/ConvictionSparkline.jsx';
import AssetCard from '../components/AssetCard.jsx';
import AssetDetailModal from '../components/AssetDetailModal.jsx';
import AddAssetButton from '../components/AddAssetButton.jsx';
import { usePage } from '../contexts/PageContext.jsx';

const STANCE_COLORS = { bullish: '#10b981', bearish: '#ef4444', neutral: '#6b7280' };

function fmtPct(x) { if (x == null) return '—'; return `${x >= 0 ? '+' : ''}${x.toFixed(2)}%`; }
function fmtPrice(p) {
  if (p == null) return '—';
  if (p < 0.01) return `$${p.toFixed(6)}`;
  if (p < 1) return `$${p.toFixed(4)}`;
  if (p < 100) return `$${p.toFixed(2)}`;
  return `$${p.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function sparklineData(sparkline) {
  return (sparkline || []).map(p => ({ value: p.value ?? p.price_usd }));
}

function assetCardProps(asset) {
  const topRec = asset.kairos_view?.recommendation;
  const topPred = asset.kairos_view?.top_prediction;
  return {
    symbol: asset.symbol,
    name: asset.name,
    category: asset.category,
    tier: asset.tier,
    asset_type: 'crypto',
    price_usd: asset.latest?.price_usd,
    change_24h: asset.latest?.change_24h,
    sparkline_data: sparklineData(asset.sparkline),
    kairos_view: (topRec || topPred) ? {
      predictions: topPred ? [topPred] : [],
      recommendation: topRec || null,
      hit_rate_30d: asset.kairos_view?.hit_rate_30d || null,
    } : null,
    themes: asset.themes || [],
    catalysts: asset.catalysts || [],
    your_position: (asset.dca_target_price || asset.holding_pct != null)
      ? { holding_pct: asset.holding_pct ?? null, target: asset.dca_target_price || null, note: asset.dca_target_note }
      : null,
  };
}

function OpportunisticCard({ asset, onAction, onClick }) {
  const price = asset.latest?.price_usd;
  const change24h = asset.latest?.change_24h;
  const daysLeft = asset.expires_at ? Math.round((new Date(asset.expires_at).getTime() - Date.now()) / 86400000) : null;

  async function promote(e) {
    e.stopPropagation();
    await fetch(`/api/crypto/assets/${asset.symbol}/promote`, { method: 'POST' });
    onAction?.();
  }
  async function dismiss(e) {
    e.stopPropagation();
    await fetch(`/api/crypto/assets/${asset.symbol}`, { method: 'DELETE' });
    onAction?.();
  }

  return (
    <div onClick={() => onClick?.(asset.symbol)} className="bg-korinth-warning/5 border border-korinth-warning/30 rounded-lg p-3 cursor-pointer hover:border-korinth-warning/60">
      <div className="flex items-center gap-2 mb-1.5">
        <Zap className="w-3.5 h-3.5 text-korinth-warning" />
        <span className="text-sm font-mono font-bold text-white">{asset.symbol}</span>
        <span className="text-[9px] text-korinth-muted ml-auto">via {asset.added_by}</span>
      </div>
      <div className="flex items-center gap-2 text-xs font-mono mb-2">
        {price ? (
          <>
            <span className="text-white">{fmtPrice(price)}</span>
            <span className={(change24h || 0) >= 0 ? 'text-korinth-accent' : 'text-korinth-danger'}>{fmtPct(change24h)}</span>
          </>
        ) : <span className="text-korinth-muted italic">No price data (not on CoinGecko)</span>}
      </div>
      <div className="text-[9px] text-korinth-muted mb-2">
        Added {new Date(asset.added_at).toLocaleDateString()}
        {daysLeft != null && ` · ${daysLeft > 0 ? `${daysLeft}d until expiry` : 'expired'}`}
      </div>
      <div className="flex gap-1.5">
        <button onClick={promote} className="flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded bg-korinth-accent/10 text-korinth-accent border border-korinth-accent/30 hover:bg-korinth-accent/20">
          <Check className="w-3 h-3" /> Promote
        </button>
        <button onClick={dismiss} className="flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded bg-korinth-danger/10 text-korinth-danger border border-korinth-danger/30 hover:bg-korinth-danger/20">
          <X className="w-3 h-3" /> Dismiss
        </button>
      </div>
    </div>
  );
}

function ThemeCard({ theme }) {
  const color = STANCE_COLORS[theme.current_stance] || '#6b7280';
  const arrow = theme.current_stance === 'bullish' ? '▲' : theme.current_stance === 'bearish' ? '▼' : '→';
  return (
    <a href="/themes" className="bg-korinth-surface border border-korinth-border rounded-lg p-3 hover:border-korinth-accent/30 transition-colors">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <span className="text-sm font-mono font-bold text-white">{theme.name}</span>
        <span className="text-[8px] uppercase px-1 py-0.5 rounded bg-korinth-border text-korinth-muted">{theme.category}</span>
        <span className="text-[8px] font-mono font-bold uppercase px-1 py-0.5 rounded" style={{ color, backgroundColor: color + '15' }}>{theme.current_stance}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-lg font-mono font-bold" style={{ color }}>{arrow} {(theme.current_conviction || 0).toFixed(1)}</span>
        <ConvictionSparkline data={theme.conviction_history} stance={theme.current_stance} width={50} height={16} />
      </div>
      <div className="text-[9px] text-korinth-muted mt-1">
        {theme.parent_count} parents · {theme.ticker_count} tickers
      </div>
    </a>
  );
}

function CatalystsFeed({ catalysts }) {
  if (!catalysts?.length) {
    return <p className="text-[10px] text-korinth-muted font-mono text-center py-3">No catalysts yet</p>;
  }
  return (
    <div className="space-y-1 max-h-96 overflow-y-auto">
      {catalysts.map(c => {
        const credPct = c.source_credibility != null ? Math.round(c.source_credibility * 100) : null;
        const credColor = credPct == null ? '#6b7280' : credPct >= 60 ? '#10b981' : credPct >= 40 ? '#f59e0b' : '#ef4444';
        const sentColor = c.sentiment === 'bullish' ? '#10b981' : c.sentiment === 'bearish' ? '#ef4444' : '#6b7280';
        return (
          <div key={c.id} className="flex items-start gap-2 px-2 py-1.5 bg-korinth-bg rounded text-[10px] font-mono">
            <span className="text-white font-bold w-16 flex-shrink-0">{c.symbol}</span>
            <span className="text-[8px] px-1 py-0.5 rounded flex-shrink-0" style={{ color: sentColor, backgroundColor: sentColor + '15' }}>{c.sentiment || 'neutral'}</span>
            <span className="text-[8px] px-1 py-0.5 rounded bg-korinth-border flex-shrink-0">{c.catalyst_type}</span>
            <span className="text-korinth-text flex-1 truncate">{c.headline}</span>
            <span className="text-[9px] text-korinth-muted flex-shrink-0">{c.source}</span>
            {credPct != null && <span className="text-[8px] font-bold px-1 rounded flex-shrink-0" style={{ color: credColor, backgroundColor: credColor + '15' }}>{credPct}%</span>}
            <span className="text-[8px] text-korinth-muted/60 flex-shrink-0">{c.observed_at?.slice(5, 16)}</span>
          </div>
        );
      })}
    </div>
  );
}

function ShadowPnLSection() {
  const [pnl, setPnl] = useState(null);
  useEffect(() => {
    fetch('/api/crypto/shadow-pnl').then(r => r.ok ? r.json() : null).then(setPnl);
  }, []);
  if (!pnl) return <div className="text-[10px] text-korinth-muted font-mono">Loading…</div>;

  if (pnl.trades_count === 0) {
    return <p className="text-[10px] text-korinth-muted font-mono italic text-center py-4">Collecting data — 0 closed crypto trades so far</p>;
  }

  const pnlColor = pnl.total_pnl_usd >= 0 ? '#10b981' : '#ef4444';
  const hitColor = pnl.hit_rate_30d != null && pnl.hit_rate_30d >= 50 ? '#10b981' : '#ef4444';

  return (
    <div className="space-y-2">
      <div className="flex gap-4 text-xs font-mono">
        <div>
          <span className="text-korinth-muted text-[9px] uppercase">Total P&L</span>
          <div className="text-lg font-bold" style={{ color: pnlColor }}>
            {pnl.total_pnl_usd >= 0 ? '+' : ''}${pnl.total_pnl_usd.toLocaleString()}
          </div>
        </div>
        <div>
          <span className="text-korinth-muted text-[9px] uppercase">Trades</span>
          <div className="text-lg font-bold text-white">{pnl.trades_count}</div>
        </div>
        <div>
          <span className="text-korinth-muted text-[9px] uppercase">Hit rate (all-time)</span>
          <div className="text-lg font-bold text-white">{pnl.hit_rate || 0}%</div>
        </div>
        {pnl.hit_rate_30d != null && (
          <div>
            <span className="text-korinth-muted text-[9px] uppercase">30d hit rate</span>
            <div className="text-lg font-bold" style={{ color: hitColor }}>{pnl.hit_rate_30d}%</div>
          </div>
        )}
      </div>
      {pnl.last_10?.length > 0 && (
        <div>
          <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-1">Last 10 trades</div>
          <div className="space-y-0.5">
            {pnl.last_10.map(t => (
              <div key={t.id} className="flex items-center gap-2 px-2 py-1 bg-korinth-bg rounded text-[10px] font-mono">
                <span className="text-white font-bold w-12">{t.asset}</span>
                <span className={t.direction === 'long' ? 'text-korinth-accent' : 'text-korinth-danger'}>{t.direction?.toUpperCase()}</span>
                <span className="text-korinth-muted">{fmtPrice(t.entry_price)} → {fmtPrice(t.exit_price)}</span>
                <span className={(t.realized_pnl_pct || 0) >= 0 ? 'text-korinth-accent' : 'text-korinth-danger'}>{fmtPct(t.realized_pnl_pct)}</span>
                <span className="text-[8px] text-korinth-muted ml-auto">{t.exit_time?.slice(5, 16)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CryptoPage() {
  const { setPage } = usePage();
  useEffect(() => { setPage('crypto'); }, []);

  const [dcaAssets, setDcaAssets] = useState([]);
  const [trackedAssets, setTrackedAssets] = useState([]);
  const [opportunistic, setOpportunistic] = useState([]);
  const [catalysts, setCatalysts] = useState([]);
  const [pulse, setPulse] = useState(null);
  const [themes, setThemes] = useState([]);
  const [modalSymbol, setModalSymbol] = useState(null);

  async function load() {
    try {
      const [dca, tracked, opp, cat, pls, thm] = await Promise.all([
        fetch('/api/crypto/assets?tier=dca_target').then(r => r.ok ? r.json() : []),
        fetch('/api/crypto/assets?tier=tracked').then(r => r.ok ? r.json() : []),
        fetch('/api/crypto/assets?tier=opportunistic').then(r => r.ok ? r.json() : []),
        fetch('/api/crypto/catalysts?limit=30').then(r => r.ok ? r.json() : []),
        fetch('/api/crypto/pulse').then(r => r.ok ? r.json() : null),
        fetch('/api/crypto/themes').then(r => r.ok ? r.json() : []),
      ]);
      setDcaAssets(dca);
      setTrackedAssets(tracked);
      setOpportunistic(opp);
      setCatalysts(cat);
      setPulse(pls);
      setThemes(thm);
    } catch {}
  }
  useEffect(() => { load(); const iv = setInterval(load, 180000); return () => clearInterval(iv); }, []);

  // Enrich assets with KAIROS view from pulse hot_24h cache where available
  const pulseKairosBySymbol = {};
  for (const h of (pulse?.hot_24h || [])) pulseKairosBySymbol[h.symbol] = h.kairos_view;

  function enrichAsset(a) {
    return { ...a, kairos_view: pulseKairosBySymbol[a.symbol] || null, themes: themes.filter(t => t.ticker_count > 0), catalysts: catalysts.filter(c => c.symbol === a.symbol).slice(0, 3) };
  }

  const biggestMover = pulse?.hot_24h?.[0];
  const accumCount = pulse?.dca_status?.filter(d => d.status === 'accumulation_zone').length || 0;
  const overexCount = pulse?.dca_status?.filter(d => d.status === 'overextended').length || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Bitcoin className="w-6 h-6 text-orange-400" />
        <h1 className="text-2xl font-bold text-white">Crypto</h1>
        <span className="text-korinth-muted text-sm">DCA · tracked · opportunistic · catalysts · themes · shadow P&L</span>
      </div>

      {/* Header summary strip */}
      <div className="bg-korinth-surface border border-korinth-border rounded-lg p-4 grid grid-cols-1 md:grid-cols-4 gap-4 text-xs font-mono">
        <div>
          <div className="text-[9px] text-korinth-muted uppercase">Biggest 24h mover</div>
          {biggestMover ? (
            <div>
              <span className="font-bold text-white">{biggestMover.symbol}</span>
              <span className={`ml-2 ${(biggestMover.change_24h || 0) >= 0 ? 'text-korinth-accent' : 'text-korinth-danger'}`}>{fmtPct(biggestMover.change_24h)}</span>
            </div>
          ) : <span className="text-korinth-muted">—</span>}
        </div>
        <div>
          <div className="text-[9px] text-korinth-muted uppercase">DCA action</div>
          <div className="text-white">
            <span className="text-korinth-accent">{accumCount}</span> accumulation ·
            <span className="text-korinth-danger ml-1">{overexCount}</span> overextended
          </div>
        </div>
        <div>
          <div className="text-[9px] text-korinth-muted uppercase">Active crypto themes</div>
          <div className="text-white">{themes.filter(t => t.status === 'active').length} active · {themes.filter(t => t.status === 'dormant').length} dormant</div>
        </div>
        <div>
          <div className="text-[9px] text-korinth-muted uppercase">Opportunistic signals</div>
          <div className="text-white font-bold">{opportunistic.length}</div>
        </div>
      </div>

      {/* DCA Tracker */}
      <ModuleContainer title="DCA Tracker" subtitle="Core accumulation targets — click card for detail">
        {dcaAssets.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {dcaAssets.map(a => (
              <AssetCard key={a.symbol} {...assetCardProps(enrichAsset(a))} on_click={() => setModalSymbol(a.symbol)} />
            ))}
          </div>
        ) : <p className="text-[10px] text-korinth-muted font-mono text-center py-4">
          No DCA assets seeded. Run: <code className="text-korinth-accent">node backend/scripts/sprint18_seed_crypto_assets.js --apply</code>
        </p>}
      </ModuleContainer>

      {/* Opportunistic — always rendered, with empty state */}
      <ModuleContainer
        title={<span className="flex items-center gap-2"><Zap className="w-4 h-4 text-korinth-warning" /> Opportunistic Signals</span>}
        subtitle={opportunistic.length > 0 ? `${opportunistic.length} small-cap calls awaiting decision` : 'Populated from BossmanCallsOfficial + similar sources'}
      >
        {opportunistic.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {opportunistic.map(a => <OpportunisticCard key={a.symbol} asset={a} onAction={load} onClick={setModalSymbol} />)}
          </div>
        ) : (
          <p className="text-[10px] text-korinth-muted font-mono text-center py-4 italic">
            No opportunistic signals yet — BossmanCallsOfficial will populate as small-cap calls arrive. New symbols mentioned in crypto_small_cap_calls channels auto-appear here with a 30-day decision window.
          </p>
        )}
      </ModuleContainer>

      {/* Tracked Assets with Add button */}
      <ModuleContainer
        title={
          <div className="flex items-center justify-between w-full gap-2">
            <span>Tracked Assets ({trackedAssets.length})</span>
            <AddAssetButton asset_type="crypto" default_tier="tracked" on_added={load} label="+ Add Crypto" />
          </div>
        }
      >
        {trackedAssets.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {trackedAssets.map(a => (
              <AssetCard key={a.symbol} {...assetCardProps(enrichAsset(a))} on_click={() => setModalSymbol(a.symbol)} />
            ))}
          </div>
        ) : (
          <p className="text-[10px] text-korinth-muted font-mono text-center py-4">No tracked assets yet. Click "+ Add Crypto" to monitor one.</p>
        )}
      </ModuleContainer>

      {/* Active Catalysts */}
      <ModuleContainer title="Active Catalysts" subtitle={`Last ${catalysts.length} crypto catalysts`}>
        <CatalystsFeed catalysts={catalysts} />
      </ModuleContainer>

      {/* Crypto Themes */}
      <ModuleContainer
        title={<span className="flex items-center gap-2"><Layers className="w-4 h-4 text-korinth-accent" /> Crypto Themes</span>}
        subtitle={themes.length > 0 ? `${themes.length} theme${themes.length > 1 ? 's' : ''} linked to crypto assets` : 'No themes link to crypto assets yet'}
      >
        {themes.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {themes.map(t => <ThemeCard key={t.id} theme={t} />)}
          </div>
        ) : (
          <p className="text-[10px] text-korinth-muted font-mono text-center py-4 italic">
            Visit <a href="/themes" className="text-korinth-accent hover:underline">/themes</a> to link existing themes to crypto tickers.
          </p>
        )}
      </ModuleContainer>

      {/* Crypto Shadow P&L */}
      <ModuleContainer
        title={<span className="flex items-center gap-2"><Activity className="w-4 h-4 text-korinth-accent" /> Crypto Shadow P&L</span>}
        subtitle="KAIROS performance on closed crypto recommendations"
      >
        <ShadowPnLSection />
      </ModuleContainer>

      {/* Detail modal */}
      {modalSymbol && (
        <AssetDetailModal
          symbol={modalSymbol}
          asset_type="crypto"
          on_close={() => setModalSymbol(null)}
          on_action={load}
        />
      )}
    </div>
  );
}
